/* eslint camelcase: ["error", {properties: "never"}] */

import React, {Fragment, useCallback, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {Fade, Container, Row, Col, Form, Button} from 'react-bootstrap';
import {Redirect, withRouter} from 'react-router-dom';
import {Formik} from 'formik';
import CommonMetaTag from './common-meta-tag';
import Configuration from '../config';
import {refreshPlayerProfile} from '../actions/player';
import {refreshPartnerProfile} from '../actions/partner';
import {showMessage} from '../actions/messages';
import {ROLES} from '../constants';
import {useTranslation} from 'react-i18next';
import satispayLogo from '../images/satispay.png';
import WalletTopUpPaymentMethods from './wallet-top-up-payment-methods';
import {getBookingTopupResume} from '../util/booking-topup-resume';
import {getBookingInvitationResume} from '../util/booking-invitation-resume';
import {clearShopTopupResume, getShopTopupResume} from '../util/shop-topup-resume';
import {FaCreditCard, FaEuroSign, FaInfoCircle, FaShieldAlt, FaWallet} from 'react-icons/fa';
import {
  clearMobilePhysicalTopup,
  hasMobilePhysicalTopup,
  MOBILE_PHYSICAL_TOPUP_RETURN_URL
} from '../util/mobile-physical-topup';

export const UserTopUpAccount = ({
  profile, token, topUpWalletSuccessPlayer, topUpWalletSuccessPartner, showNotClosePaypalMessage, showInvalidAmountMessage, showSatispayPendingMessage, showSatispayErrorMessage, showPaymentErrorMessage, location, history
}) => {
  const {t} = useTranslation();
  const [satispayLoading, setSatispayLoading] = useState(false);
  const [nativeReturnReady, setNativeReturnReady] = useState(false);
  const requestParams = new URLSearchParams(location.search || '');
  const repPreparedMethod = requestParams.get('from') === 'rep' ? requestParams.get('method') : null;
  const nativePhysicalTopup = hasMobilePhysicalTopup();

  const isValidTopupAmount = (amount) => {
    return amount === undefined || amount === "" || (amount >= 5 && amount <= 9999);
  };

  const topUpWalletSuccess = useCallback(async () => {
    if (!profile || !profile.user) return;
    if (profile.user.role === ROLES.player) {
      await topUpWalletSuccessPlayer();
    } else {
      await topUpWalletSuccessPartner();
    }
    if (nativePhysicalTopup) {
      clearMobilePhysicalTopup();
      setNativeReturnReady(true);
      window.location.href = MOBILE_PHYSICAL_TOPUP_RETURN_URL;
      return;
    }
    const bookingResume = getBookingTopupResume();
    const bookingInvitation = getBookingInvitationResume();
    const shopResume = getShopTopupResume();
    if (bookingInvitation) {
      history.push(`/booking-invitation/${bookingInvitation}`);
    } else if (bookingResume && bookingResume.returnPath) {
      history.push(bookingResume.returnPath);
    } else if (shopResume && shopResume.returnPath) {
      clearShopTopupResume();
      history.push(shopResume.returnPath);
    }
  }, [history, nativePhysicalTopup, profile, topUpWalletSuccessPlayer, topUpWalletSuccessPartner]);

  useEffect(() => {
    if (!profile || !token || !location || !location.search) {
      return;
    }
    const params = new URLSearchParams(location.search);
    const transactionId = params.get('satispay');
    if (!transactionId) {
      return;
    }

    fetch(`${Configuration.apiEndPoint}/satispay/payments/${transactionId}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`
      },
      mode: 'cors'
    }).then(res => res.json())
      .then(data => {
        if (data.status === 'completed') {
          topUpWalletSuccess();
        } else if (data.status === 'pending') {
          showSatispayPendingMessage();
        } else {
          showSatispayErrorMessage();
        }
      })
      .catch(() => showSatispayErrorMessage());
  }, [profile, token, location, showSatispayErrorMessage, showSatispayPendingMessage, topUpWalletSuccess]);

  if (profile === null) {
    const returnPath = `${location.pathname || '/user-top-up-account'}${location.search || ''}`;
    return (
      <Redirect to={`/login?next=${encodeURIComponent(returnPath)}`}/>
    );
  }


  return (
    <div>
    <Formik
      enableReinitialize
      initialValues={Object.assign({}, profile, {
        topupAmount: new URLSearchParams(location.search || '').get('amount') ||
          (getShopTopupResume() || {}).amount ||
          (getBookingTopupResume() || {}).amount ||
          profile.topupAmount
      })}
    >
      {({
        handleChange,
        values,
      }) => {
        return (
          <Fragment>
            <CommonMetaTag/>
            <Fade in appear className="wallet-topup-page" style={{transition: 'opacity 0.30s linear'}}>
              <Container>
                <Row className="justify-content-center">
                  <Col xs={12} lg={9} xl={8}>
                    <div className="wallet-topup-hero">
                      <div className="wallet-topup-hero__icon" aria-hidden="true"><FaWallet/></div>
                      <div><span>{t('TOPUP_REPLAYER_CREDIT')}</span><h1>{t('ACCOUNT_TOPUP')}</h1><p>{t('TOPUP_REPLAYER_CREDIT_BODY')}</p></div>
                      <div className="wallet-topup-balance"><small>{t('MY_CREDIT')}</small><strong><FaEuroSign aria-hidden="true"/> {values.user.wallet}</strong></div>
                    </div>
                    <div className="wallet-topup-card">
                      {nativePhysicalTopup ? <div className="wallet-topup-rep-prepared" role="status"><strong>{t('MOBILE_PHYSICAL_TOPUP_TITLE')}</strong><span>{t('MOBILE_PHYSICAL_TOPUP_BODY')}</span></div> : null}
                      {nativeReturnReady ? <Button className="mb-3" onClick={() => { window.location.href = MOBILE_PHYSICAL_TOPUP_RETURN_URL; }}>{t('MOBILE_PHYSICAL_TOPUP_RETURN')}</Button> : null}
                      {repPreparedMethod ? <div className="wallet-topup-rep-prepared" role="status"><strong>{t('TOPUP_REP_PREPARED_TITLE')}</strong><span>{t('TOPUP_REP_PREPARED_BODY')}</span></div> : null}
                      <div className="wallet-topup-card__heading"><div className="wallet-topup-step">1</div><div><span>{t('TOPUP_AMOUNT_TITLE')}</span><p>{t('TOPUP_AMOUNT_BODY')}</p></div></div>
                      <Form>
                        <Form.Group className="wallet-topup-amount">
                          <Form.Label>{t('ACCOUNT_TOPUP')} (€)</Form.Label>
                          <div className="wallet-topup-amount__control"><FaEuroSign aria-hidden="true"/><Form.Control required type="number" name="topupAmount" value={values.topupAmount || ''} step={1} min={5} max={9999} onChange={handleChange}/></div>
                          <Form.Text><FaInfoCircle aria-hidden="true"/> {t('MIN_MAX',{min: '5,00 €', max: '9999,00 €'})}</Form.Text>
                        </Form.Group>
                        <div className="wallet-topup-divider"/>
                        <div className="wallet-topup-card__heading"><div className="wallet-topup-step">2</div><div><span>{t('TOPUP_PAYMENT_TITLE')}</span><p>{t('TOPUP_PAYMENT_BODY')}</p></div><FaCreditCard className="wallet-topup-card__heading-icon" aria-hidden="true"/></div>
                        <div className="wallet-topup-methods-panel">
                          <WalletTopUpPaymentMethods
                            amount={values.topupAmount}
                            token={token}
                            isValidAmount={isValidTopupAmount}
                            onInvalidAmount={showInvalidAmountMessage}
                            onPending={showNotClosePaypalMessage}
                            onSuccess={topUpWalletSuccess}
                            onError={showPaymentErrorMessage}
                          />
                          {Configuration.satispayEnabled !== false ? <Button
                            className={`satispay-topup-button mt-3${repPreparedMethod === 'satispay' ? ' is-rep-selected' : ''}`}
                            disabled={satispayLoading || !isValidTopupAmount(values.topupAmount)}
                            onClick={() => {
                          if (!isValidTopupAmount(values.topupAmount)) {
                            showInvalidAmountMessage();
                            return;
                          }
                          setSatispayLoading(true);
                          fetch(`${Configuration.apiEndPoint}/satispay/payments`, {
                            method: 'POST',
                            headers: {
                              'content-type': 'application/json',
                              Accept: 'application/json',
                              Authorization: `Bearer ${token}`
                            },
                            mode: 'cors',
                            body: JSON.stringify({
                              amount: values && values.topupAmount ? Number(values.topupAmount) : 5
                            })
                          }).then(res => res.json())
                            .then(data => {
                              if (data.redirectUrl) {
                                window.location.href = data.redirectUrl;
                              } else {
                                setSatispayLoading(false);
                                showSatispayErrorMessage();
                              }
                            })
                            .catch(() => {
                              setSatispayLoading(false);
                              showSatispayErrorMessage();
                            });
                            }}
                          >
                            <img src={satispayLogo} alt="Satispay" />
                            <span>{satispayLoading ? 'Apertura Satispay...' : `${repPreparedMethod === 'satispay' ? 'Continua' : 'Paga'} con Satispay${repPreparedMethod === 'satispay' && values.topupAmount ? ` · € ${Number(values.topupAmount).toFixed(2).replace('.', ',')}` : ''}`}</span>
                          </Button> : null}
                        </div>
                      </Form>
                    </div>
                    <div className="wallet-topup-note"><FaShieldAlt aria-hidden="true"/><div><strong>{t('WARNING')}</strong><p>{t('ACCOUNT_TOPUP_NOTES')}</p></div></div>
                  </Col>
                </Row>
              </Container>
            </Fade>
          </Fragment>
        );
      }}
    </Formik>
  </div>
  );
};

UserTopUpAccount.propTypes = {
  profile: PropTypes.object,
  token: PropTypes.string,
  topUpWalletSuccessPlayer: PropTypes.func.isRequired,
  topUpWalletSuccessPartner: PropTypes.func.isRequired,
  showNotClosePaypalMessage: PropTypes.func.isRequired,
  showInvalidAmountMessage: PropTypes.func.isRequired,
  showSatispayPendingMessage: PropTypes.func.isRequired,
  showSatispayErrorMessage: PropTypes.func.isRequired,
  showPaymentErrorMessage: PropTypes.func.isRequired,
  location: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired
};

UserTopUpAccount.defaultProps = {
  profile: null,
  token: null
};

const mapStateToProps = ({login: {profile, token}}) => ({
  profile,
  token
});

const mapDispatchToProps = dispatch => ({
  topUpWalletSuccessPlayer: async () => {
    await dispatch(refreshPlayerProfile());
    dispatch(showMessage('ACCOUNT_TOPUP', 'ACCOUNT_TOPUP_SUCCESSFUL'));
  },
  topUpWalletSuccessPartner: async () => {
    await dispatch(refreshPartnerProfile());
    dispatch(showMessage('ACCOUNT_TOPUP', 'ACCOUNT_TOPUP_SUCCESSFUL'));
  },
  showNotClosePaypalMessage: () => {
    dispatch(showMessage('ACCOUNT_TOPUP', 'ACCOUNT_TOPUP_ORDER_IN_PROGRESS'));
  },
  showInvalidAmountMessage: () => {
    dispatch(showMessage('ACCOUNT_TOPUP', 'MIN_MAX',{min: '5,00 €', max: '9999,00 €'}));
  },
  showSatispayPendingMessage: () => {
    dispatch(showMessage('ACCOUNT_TOPUP', 'SATISPAY_PAYMENT_PENDING'));
  },
  showSatispayErrorMessage: () => {
    dispatch(showMessage('ACCOUNT_TOPUP', 'SATISPAY_PAYMENT_ERROR'));
  },
  showPaymentErrorMessage: () => {
    dispatch(showMessage('ACCOUNT_TOPUP', 'ACCOUNT_TOPUP_FAILED'));
  },
});

export default withRouter(connect(
  mapStateToProps,
  mapDispatchToProps
)(UserTopUpAccount));
